import type { ReactNode } from 'react';

import { Sidebar } from '@/components/layout/Sidebar';
import { Topbar } from '@/components/layout/Topbar';
import { EmailVerificationBanner } from '@/components/patterns/EmailVerificationBanner';
import { useCurrentProfile } from '@/hooks/useCurrentProfile';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/uiStore';

interface AppShellProps {
  /** Page heading shown in the Topbar. */
  title: string;
  /** Optional line under the heading in the Topbar. */
  subtitle?: string;
  /**
   * Route the Topbar's back chevron goes to. Pass it only on a page that lives inside
   * another (a session's detail → "/sessions"); omit it on a top-level page and no
   * chevron is rendered.
   */
  backTo?: string;
  /**
   * The page's primary controls, rendered at the right of the Topbar row (see Topbar `actions`).
   *
   * Pass these instead of building a second header inside the page body — which is what the
   * Workflows and Questionnaires lists both did, printing their heading twice.
   */
  actions?: ReactNode;
  /** Page content. Lives inside `<main>` and gets the standard 16 px padding. */
  children: ReactNode;
  /** Hide the Topbar entirely — use for full-screen editor layouts that provide their own header. */
  hideTopbar?: boolean;
  /** Remove padding from `<main>` and make it a flex column — use with hideTopbar for full-height editors. */
  noPadding?: boolean;
}

// Standard authenticated layout: full-width top banner → row of sidebar
// (in-flow on desktop, off-canvas on mobile) + content column.
//
// Layout decisions:
//   * EmailVerificationBanner sits OUTSIDE the sidebar+content flex row so
//     it spans the entire viewport width (covers the space above both rails).
//     It self-hides once the user's email is verified — no per-page opt-in needed.
//   * Sidebar is a flex child on lg+ (`sticky top-0` so it stays pinned
//     when the page scrolls). On <lg it switches to `fixed` to overlay
//     content as an off-canvas drawer — the flex row stops reserving
//     space for it there, and the backdrop catches taps to close.
//   * Topbar uses `sticky top-0` inside the content column so the page
//     header stays visible while the body scrolls.
export function AppShell({
  title,
  subtitle,
  backTo,
  actions,
  children,
  hideTopbar,
  noPadding,
}: AppShellProps) {
  const mobileSidebarOpen = useUiStore((s) => s.mobileSidebarOpen);
  const setMobileSidebarOpen = useUiStore((s) => s.setMobileSidebarOpen);
  // Fetch Studio's DB-resolved profile on every authenticated screen.
  // Syncs tenantStore with the resolved tenantId so useMyPermissions and
  // the X-Tenant-Id header interceptor see the correct tenant even when
  // the JWT carries no tenant_id claim.
  useCurrentProfile();

  return (
    <div
      className={cn(
        'admin-page flex flex-col',
        // Full-screen editor pages must be EXACTLY the viewport height so inner
        // panels scroll (min-h-dvh would let content grow the page and defeat
        // every inner overflow-hidden). Normal pages keep natural body scroll.
        noPadding ? 'h-dvh' : 'min-h-dvh',
      )}
    >
      <EmailVerificationBanner />
      <div className="flex flex-1 min-h-0 relative">
        <Sidebar />

        {/* Mobile drawer backdrop. Hidden on lg+ where the sidebar lives
            in-flow. Sits beneath the sidebar (z-10) but above content. */}
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileSidebarOpen(false)}
          className={cn(
            'fixed inset-0 z-[9] bg-black/45 lg:hidden',
            mobileSidebarOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
            'transition-opacity duration-300',
          )}
          tabIndex={mobileSidebarOpen ? 0 : -1}
        />

        <div
          className={cn(
            'content-area flex-1 min-w-0 flex flex-col',
            // Full-screen editors (noPadding) must clamp to the viewport so
            // inner panels scroll instead of the whole page. Normal pages keep
            // their natural height + body scroll.
            noPadding && 'min-h-0 overflow-hidden',
          )}
        >
          {!hideTopbar && (
            <Topbar title={title} subtitle={subtitle} backTo={backTo} actions={actions} />
          )}
          <main
            className={cn('flex-1 min-h-0', noPadding ? 'overflow-hidden flex flex-col' : 'p-4')}
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
